import type { StoryView } from "../shared/story";

/**
 * Ink that dries: a tappable name is marked by how much it matters now. Wet: it entered the story in this turn, and
 * the turn is the latest or the one before. Live: it is what an open thread is about (its name is in the thread's
 * name). Ink older than the last three turns is always dry. Dry: plain text that still opens on tap, shows its line on hover, and shows it when the
 * reader long-presses the paragraph. When a chapter ends, all of its ink dries at once (the turn that ended it included).
 */
export type Ink = "wet" | "live" | "dry";
const bare = (s: string) => s.toLowerCase().replace(/^(the|a|an)\s+/, "").trim();
export function inkOf(view: StoryView | null, turn: number): (name: string) => Ink {
  if (!view) return () => "wet";
  const latest = Math.max(view.n, ...view.turns.map((t) => t.n));
  const byName = new Map(view.things.map((t) => [bare(t.name), t]));
  const threadText = view.threads.filter((t) => t.state === "open").map((t) => t.name.toLowerCase()).join(" | ");
  const lastEnd = Math.max(0, ...view.turns.filter((t) => t.chapterEnd || t.storyEnd).map((t) => t.n));
  return (name) => {
    if (turn <= lastEnd) return "dry";
    const t = byName.get(bare(name));
    if (t && t.turn === turn && turn >= latest - 1) return "wet";
    // Older than the last three turns, all ink has dried.
    if (turn >= latest - 2 && threadText.includes(bare(name))) return "live";
    return "dry";
  };
}
